import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, updateDoc, where, Timestamp, writeBatch } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { auth, db } from '../../firebase'
import {
  Box,
  Button,
  Stack,
  Typography,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Checkbox
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  DeleteForever as DeleteContentIcon,
  Block as BlockUserIcon,
  ThumbUp as KeepIcon,
  ImageNotSupported as NoImageIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteRecordIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material'

type SourceKind = 'user_report' | 'auto_vision'
type QueueStatus = 'pending' | 'resolved_removed' | 'resolved_kept'
type StatusFilter = QueueStatus | 'all'
type SourceFilter = SourceKind | 'all'

/** صف موحّد يمثل بلاغ مستخدم (request_reports) أو علامة فحص تلقائي (moderation_flags) */
interface QueueRow {
  id: string
  collectionName: 'request_reports' | 'moderation_flags'
  createdAt?: any
  source: SourceKind
  status: string
  imageUrl: string
  deletedFromStorage: boolean
  reason: string
  targetType: string
  targetId: string
  requestId?: string
  offerId?: string
  ownerUserId: string
  ownerUserName: string
  ownerUserPhone: string
  reporterId?: string
  reporterName?: string
  reporterPhone?: string
  reporterRole?: string
  contentSnapshot?: {
    productName?: string
    text?: string
    images?: string[]
    offerPrice?: number | null
    offerNotes?: string
    requestNotes?: string
    additionalDetails?: string
  }
  visionScores?: Record<string, string>
  reviewedBy?: string
  reviewedAt?: any
  reviewNote?: string
  storagePath?: string
}

type PersonInfo = { id: string; name: string; phone: string; status: 'active' | 'inactive' }

type ContentDetails = {
  text: string
  images: string[]
  requestId: string
  productName: string
  offerPrice?: number | null
  offerNotes?: string
  requestNotes?: string
  additionalDetails?: string
}

const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  'pending': { label: 'قيد المراجعة', color: 'warning' },
  'resolved_removed': { label: 'تم حذف المحتوى', color: 'error' },
  'resolved_kept': { label: 'تم رفض البلاغ', color: 'success' },
  'resolved': { label: 'تمت المعالجة', color: 'success' }
}

const targetTypeLabels: Record<string, string> = {
  'request': 'طلب مشتري',
  'offer': 'عرض تاجر',
  'user_profile': 'صورة ملف شخصي',
  'store_activation': 'طلب تفعيل متجر',
  'product': 'منتج',
  'unknown': 'غير محدد'
}

const reasonLabels: Record<string, string> = {
  'inappropriate_content': 'محتوى غير لائق',
  'inappropriate_offer': 'عرض غير لائق'
}

/** ترجمة سبب الفحص التلقائي مثل adult:VERY_LIKELY */
function formatReason(reason: string): string {
  if (!reason) return 'غير محدد'
  if (reasonLabels[reason]) return reasonLabels[reason]
  const [kind, likelihood] = reason.split(':')
  const kinds: Record<string, string> = {
    adult: 'محتوى إباحي',
    violence: 'عنف',
    racy: 'محتوى مثير',
    spoof: 'محتوى مضلل',
    medical: 'محتوى طبي صادم'
  }
  const levels: Record<string, string> = {
    VERY_LIKELY: 'مرجّح جداً',
    LIKELY: 'مرجّح',
    POSSIBLE: 'محتمل',
    UNLIKELY: 'غير مرجّح',
    VERY_UNLIKELY: 'غير مرجّح إطلاقاً'
  }
  if (kind && likelihood && kinds[kind]) {
    return `${kinds[kind]} (${levels[likelihood] || likelihood})`
  }
  return kinds[reason] || reason
}

export default function AbuseReportsPage() {
  const navigate = useNavigate()
  const [flagRows, setFlagRows] = useState<QueueRow[]>([])
  const [reportRows, setReportRows] = useState<QueueRow[]>([])
  const [flagsError, setFlagsError] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; row?: QueueRow }>({ open: false })
  const [imageDialog, setImageDialog] = useState<{ open: boolean; url?: string }>({ open: false })
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    row?: QueueRow
    action?: 'remove' | 'deactivate' | 'activate' | 'keep'
  }>({ open: false })
  const [reviewNote, setReviewNote] = useState('')
  const [working, setWorking] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; rows: QueueRow[] }>({ open: false, rows: [] })
  /** حالة المستخدمين الحية (نفس مصدر صفحة المستخدمين) */
  const [liveUsers, setLiveUsers] = useState<Record<string, PersonInfo>>({})

  const rowKey = (row: QueueRow) => `${row.collectionName}:${row.id}`

  const roleLabel = (role?: string, fallbackTargetType?: string) => {
    if (role === 'buyer') return 'مشتري'
    if (role === 'seller') return 'تاجر'
    if (fallbackTargetType === 'offer') return 'مشتري'
    if (fallbackTargetType === 'request') return 'تاجر'
    return ''
  }

  const normalizeUserStatus = (d: any): 'active' | 'inactive' => {
    if (d?.status === 'inactive') return 'inactive'
    if (d?.status === 'active') return 'active'
    if (d?.isActive === false) return 'inactive'
    return 'active'
  }

  const [contentByKey, setContentByKey] = useState<Record<string, ContentDetails>>({})
  const [targetOwners, setTargetOwners] = useState<Record<string, PersonInfo>>({})
  const [reportersById, setReportersById] = useState<Record<string, PersonInfo>>({})

  const openUserAccount = (userId?: string, phone?: string) => {
    const q = (userId || phone || '').trim()
    if (!q) return
    navigate(`/users?userId=${encodeURIComponent(q)}`)
  }

  const openRequestOrders = (requestId?: string) => {
    const q = (requestId || '').trim()
    if (!q) return
    navigate(`/orders?orderId=${encodeURIComponent(q)}&q=${encodeURIComponent(q)}`)
  }

  useEffect(() => {
    const q = query(collection(db, 'request_reports'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setReportRows(
          snapshot.docs.map((d) => {
            const data = d.data() as any
            const offerId = String(data.offerId || '').trim()
            const targetType =
              data.targetType === 'offer' || offerId
                ? 'offer'
                : data.targetType === 'request'
                  ? 'request'
                  : offerId
                    ? 'offer'
                    : 'request'
            const targetId =
              String(data.targetId || '').trim() ||
              (targetType === 'offer' ? offerId : String(data.requestId || ''))
            return {
              id: d.id,
              collectionName: 'request_reports' as const,
              createdAt: data.createdAt,
              source: 'user_report' as const,
              status: data.status || 'pending',
              imageUrl: Array.isArray(data.contentSnapshot?.images)
                ? String(data.contentSnapshot.images[0] || '')
                : '',
              deletedFromStorage: false,
              reason: data.reason || 'inappropriate_content',
              targetType,
              targetId,
              requestId: String(data.requestId || '').trim() || undefined,
              offerId: offerId || undefined,
              ownerUserId: String(data.reportedUserId || data.ownerUserId || ''),
              ownerUserName: String(data.reportedUserName || ''),
              ownerUserPhone: String(data.reportedUserPhone || ''),
              reporterId: data.reporterId || '',
              reporterName: String(data.reporterName || ''),
              reporterPhone: String(data.reporterPhone || ''),
              reporterRole: String(data.reporterRole || ''),
              contentSnapshot: data.contentSnapshot || undefined,
              reviewedBy: data.reviewedBy,
              reviewedAt: data.reviewedAt,
              reviewNote: data.reviewNote
            }
          })
        )
      },
      (error) => {
        console.warn('request_reports listener error:', error)
        setReportRows([])
      }
    )
    return () => unsub()
  }, [])

  useEffect(() => {
    const q = query(collection(db, 'moderation_flags'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setFlagsError('')
        setFlagRows(
          snapshot.docs
            // بلاغات المستخدمين تُعرض من request_reports فقط — تجنّب التكرار
            .filter((d) => (d.data() as any)?.source !== 'user_report')
            .map((d) => {
            const data = d.data() as any
            return {
              id: d.id,
              collectionName: 'moderation_flags' as const,
              createdAt: data.createdAt,
              source: 'auto_vision' as SourceKind,
              status: data.status || 'pending',
              imageUrl: data.imageUrl || '',
              deletedFromStorage: data.deletedFromStorage === true,
              reason: data.reason || '',
              targetType: data.targetType || 'unknown',
              targetId: data.targetId || '',
              ownerUserId: data.ownerUserId || '',
              ownerUserName: data.ownerUserName || '',
              ownerUserPhone: data.ownerUserPhone || '',
              visionScores: data.visionScores || undefined,
              reviewedBy: data.reviewedBy,
              reviewedAt: data.reviewedAt,
              reviewNote: data.reviewNote,
              storagePath: data.storagePath
            }
          })
        )
      },
      (error) => {
        // المجموعة قد لا تكون موجودة بعد أو القواعد لم تُنشر — لا نُعطل الصفحة
        console.warn('moderation_flags listener error:', error)
        setFlagRows([])
        setFlagsError('تعذّر قراءة نتائج الفحص التلقائي حالياً. سيتم عرضها تلقائياً بمجرد توفرها.')
      }
    )
    return () => unsub()
  }, [])

  const rows = useMemo(() => {
    const merged = [...reportRows, ...flagRows]
    merged.sort((a, b) => {
      const av = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0
      const bv = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0
      return bv - av
    })
    return merged
  }, [reportRows, flagRows])

  /** معرّفات المستخدمين المرتبطين بالبلاغات — استماع حي لنفس حالة صفحة المستخدمين */
  const watchedUserIds = useMemo(() => {
    const ids = new Set<string>()
    rows.forEach((r) => {
      if (r.ownerUserId) ids.add(r.ownerUserId)
      if (r.reporterId) ids.add(r.reporterId)
    })
    Object.values(targetOwners).forEach((o) => {
      if (o.id) ids.add(o.id)
    })
    return [...ids]
  }, [rows, targetOwners])

  useEffect(() => {
    if (watchedUserIds.length === 0) return
    const unsubs = watchedUserIds.map((userId) =>
      onSnapshot(
        doc(db, 'users', userId),
        (snap) => {
          if (!snap.exists()) return
          const d = snap.data() as any
          setLiveUsers((prev) => ({
            ...prev,
            [userId]: {
              id: userId,
              name: d.name || d.fullName || prev[userId]?.name || 'غير محدد',
              phone: d.phone || d.phoneNumber || prev[userId]?.phone || 'غير متوفر',
              status: normalizeUserStatus(d)
            }
          }))
        },
        () => undefined
      )
    )
    return () => unsubs.forEach((u) => u())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedUserIds.join('|')])

  // جلب نص/صور المحتوى المُبلَّغ عنه + بيانات صاحب المحتوى والمبلّغ
  useEffect(() => {
    let cancelled = false

    const extractImages = (d: any): string[] => {
      const out: string[] = []
      const push = (v: unknown) => {
        const s = String(v || '').trim()
        if (s && (s.startsWith('http') || s.startsWith('gs://'))) out.push(s)
      }
      push(d?.categoryImageUrl)
      push(d?.imageUrl)
      if (Array.isArray(d?.productSpecs?.imageUrls)) d.productSpecs.imageUrls.forEach(push)
      if (Array.isArray(d?.images)) d.images.forEach(push)
      return [...new Set(out)]
    }

    const loadUser = async (userId: string): Promise<PersonInfo | undefined> => {
      if (!userId) return undefined
      try {
        const userSnap = await getDoc(doc(db, 'users', userId))
        const d = userSnap.exists() ? (userSnap.data() as any) : {}
        return {
          id: userId,
          name: d.name || d.fullName || 'غير محدد',
          phone: d.phone || d.phoneNumber || 'غير متوفر',
          status: normalizeUserStatus(d)
        }
      } catch {
        return { id: userId, name: 'غير محدد', phone: 'غير متوفر', status: 'active' }
      }
    }

    const load = async () => {
      for (const row of rows) {
        const key = `${row.targetType}:${row.targetId}`
        if (!row.targetId) continue

        // لقطة محفوظة عند إنشاء البلاغ (أولوية)
        const snap = row.contentSnapshot
        if (!contentByKey[key] && snap) {
          const images = Array.isArray(snap.images) ? snap.images.filter(Boolean) : []
          if (!cancelled) {
            setContentByKey((prev) => ({
              ...prev,
              [key]: {
                text: snap.text || '',
                images,
                requestId: row.requestId || (row.targetType === 'request' ? row.targetId : ''),
                productName: snap.productName || '',
                offerPrice: snap.offerPrice,
                offerNotes: snap.offerNotes,
                requestNotes: snap.requestNotes,
                additionalDetails: snap.additionalDetails
              }
            }))
          }
        }

        if (!contentByKey[key] && !snap) {
          let text = ''
          let ownerId = row.ownerUserId
          let images: string[] = []
          let requestId = row.requestId || ''
          let productName = ''
          let offerPrice: number | null | undefined
          let offerNotes = ''
          let requestNotes = ''
          let additionalDetails = ''

          try {
            if (row.targetType === 'request') {
              const reqSnap = await getDoc(doc(db, 'requests', row.targetId))
              if (reqSnap.exists()) {
                const d = reqSnap.data() as any
                productName = d.productName || ''
                requestNotes = d.notes || ''
                additionalDetails = d.additionalProductDetails || ''
                text = [productName, requestNotes, additionalDetails].filter(Boolean).join('\n')
                ownerId = ownerId || d.buyerId || ''
                images = extractImages(d)
                requestId = row.targetId
              } else {
                text = 'المحتوى محذوف أو غير موجود'
              }
            } else if (row.targetType === 'offer') {
              const offerSnap = await getDoc(doc(db, 'offers', row.targetId))
              if (offerSnap.exists()) {
                const d = offerSnap.data() as any
                ownerId = ownerId || d.sellerId || ''
                offerNotes = d.notes || ''
                offerPrice = typeof d.price === 'number' ? d.price : Number(d.price) || null
                requestId = String(d.requestId || row.requestId || '')
                text = [offerNotes ? `نص العرض: ${offerNotes}` : '', offerPrice != null ? `السعر: ${offerPrice}` : '']
                  .filter(Boolean)
                  .join('\n')
                if (requestId) {
                  const reqSnap = await getDoc(doc(db, 'requests', requestId))
                  if (reqSnap.exists()) {
                    const rd = reqSnap.data() as any
                    productName = rd.productName || ''
                    requestNotes = rd.notes || ''
                    additionalDetails = rd.additionalProductDetails || ''
                    images = extractImages(rd)
                    text = [
                      text,
                      productName ? `الطلب: ${productName}` : '',
                      requestNotes ? `ملاحظات الطلب: ${requestNotes}` : '',
                      additionalDetails ? `تفاصيل إضافية: ${additionalDetails}` : ''
                    ]
                      .filter(Boolean)
                      .join('\n')
                  }
                }
              } else {
                text = 'المحتوى محذوف أو غير موجود'
              }
            } else if (row.targetType === 'product') {
              const prodSnap = await getDoc(doc(db, 'products', row.targetId))
              if (prodSnap.exists()) {
                const d = prodSnap.data() as any
                text = [d.name, d.description].filter(Boolean).join(' — ')
                images = extractImages(d)
              } else {
                text = 'المحتوى محذوف أو غير موجود'
              }
            } else if (row.imageUrl) {
              images = [row.imageUrl]
            }
          } catch (e) {
            console.warn('تعذر جلب المحتوى المُبلَّغ عنه:', e)
          }

          if (!cancelled) {
            setContentByKey((prev) => ({
              ...prev,
              [key]: {
                text,
                images,
                requestId,
                productName,
                offerPrice,
                offerNotes,
                requestNotes,
                additionalDetails
              }
            }))
          }

          if (ownerId && !targetOwners[key]) {
            const owner =
              row.ownerUserName || row.ownerUserPhone
                ? {
                    id: ownerId,
                    name: row.ownerUserName || 'غير محدد',
                    phone: row.ownerUserPhone || 'غير متوفر',
                    status: 'active' as const
                  }
                : await loadUser(ownerId)
            if (owner && !cancelled) {
              setTargetOwners((prev) => ({ ...prev, [key]: owner }))
            }
          }
        } else if (row.ownerUserId && !targetOwners[key]) {
          const owner =
            row.ownerUserName || row.ownerUserPhone
              ? {
                  id: row.ownerUserId,
                  name: row.ownerUserName || 'غير محدد',
                  phone: row.ownerUserPhone || 'غير متوفر',
                  status: 'active' as const
                }
              : await loadUser(row.ownerUserId)
          if (owner && !cancelled) {
            setTargetOwners((prev) => ({ ...prev, [key]: owner }))
          }
        }

        if (row.reporterId && !reportersById[row.reporterId]) {
          const reporter =
            row.reporterName || row.reporterPhone
              ? {
                  id: row.reporterId,
                  name: row.reporterName || 'غير محدد',
                  phone: row.reporterPhone || 'غير متوفر',
                  status: 'active' as const
                }
              : await loadUser(row.reporterId)
          if (reporter && !cancelled) {
            setReportersById((prev) => ({ ...prev, [row.reporterId!]: reporter }))
          }
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  const resolveOwner = (row: QueueRow): PersonInfo => {
    const key = `${row.targetType}:${row.targetId}`
    const fetched = targetOwners[key]
    const id = row.ownerUserId || fetched?.id || ''
    const live = id ? liveUsers[id] : undefined
    return {
      id,
      name: live?.name || row.ownerUserName || fetched?.name || 'غير محدد',
      phone: live?.phone || row.ownerUserPhone || fetched?.phone || 'غير متوفر',
      status: live?.status || fetched?.status || 'active'
    }
  }

  const resolveReporter = (row: QueueRow): PersonInfo => {
    const id = row.reporterId || ''
    const fetched = id ? reportersById[id] : undefined
    const live = id ? liveUsers[id] : undefined
    return {
      id,
      name: live?.name || row.reporterName || fetched?.name || (id ? id : 'غير محدد'),
      phone: live?.phone || row.reporterPhone || fetched?.phone || 'غير متوفر',
      status: live?.status || fetched?.status || 'active'
    }
  }

  const resolveContent = (row: QueueRow): ContentDetails => {
    const key = `${row.targetType}:${row.targetId}`
    const cached = contentByKey[key]
    if (cached) return cached
    const snap = row.contentSnapshot
    const images = [
      ...(Array.isArray(snap?.images) ? snap!.images!.filter(Boolean) : []),
      ...(row.imageUrl ? [row.imageUrl] : [])
    ]
    return {
      text: snap?.text || '',
      images: [...new Set(images)],
      requestId: row.requestId || (row.targetType === 'request' ? row.targetId : ''),
      productName: snap?.productName || '',
      offerPrice: snap?.offerPrice,
      offerNotes: snap?.offerNotes,
      requestNotes: snap?.requestNotes,
      additionalDetails: snap?.additionalDetails
    }
  }

  const resolveText = (row: QueueRow) => resolveContent(row).text
  const resolveImages = (row: QueueRow) => {
    const images = resolveContent(row).images
    if (images.length) return images
    return row.imageUrl ? [row.imageUrl] : []
  }
  const resolveImage = (row: QueueRow) => resolveImages(row)[0] || ''

  const filteredRows = rows.filter((row) => {
    const matchesStatus = statusFilter === 'all' || row.status === statusFilter
    const matchesSource = sourceFilter === 'all' || row.source === sourceFilter
    const owner = resolveOwner(row)
    const reporter = resolveReporter(row)
    const content = resolveContent(row)
    const q = searchQuery.trim().toLowerCase()
    const matchesSearch =
      !q ||
      owner.name.toLowerCase().includes(q) ||
      owner.phone.includes(searchQuery.trim()) ||
      owner.id.toLowerCase().includes(q) ||
      reporter.name.toLowerCase().includes(q) ||
      reporter.phone.includes(searchQuery.trim()) ||
      reporter.id.toLowerCase().includes(q) ||
      row.reason.toLowerCase().includes(q) ||
      row.targetId.toLowerCase().includes(q) ||
      (content.requestId || '').toLowerCase().includes(q) ||
      resolveText(row).toLowerCase().includes(q)
    return matchesStatus && matchesSource && matchesSearch
  })

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filteredRows.length / pageSize)
  const pendingCount = rows.filter((r) => r.status === 'pending').length

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'غير محدد'
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'غير محدد'
    }
  }

  const currentAdmin = () => auth.currentUser?.email || auth.currentUser?.uid || 'admin'

  const markReviewed = async (row: QueueRow, newStatus: QueueStatus, note: string) => {
    await updateDoc(doc(db, row.collectionName, row.id), {
      status: newStatus,
      reviewedBy: currentAdmin(),
      reviewedAt: Timestamp.now(),
      reviewNote: note || ''
    })

    // إن وُجدت علامات قديمة من بلاغ مستخدم لنفس الهدف — أغلقها دون حذف السجل
    if (row.collectionName === 'request_reports' && row.targetId) {
      try {
        const linked = await getDocs(
          query(
            collection(db, 'moderation_flags'),
            where('source', '==', 'user_report'),
            where('targetId', '==', row.targetId),
            where('status', '==', 'pending')
          )
        )
        await Promise.all(
          linked.docs.map((d) =>
            updateDoc(d.ref, {
              status: newStatus,
              reviewedBy: currentAdmin(),
              reviewedAt: Timestamp.now(),
              reviewNote: note || 'أُغلق مع بلاغ المستخدم'
            })
          )
        )
      } catch (e) {
        console.warn('تعذر مزامنة moderation_flags المرتبطة:', e)
      }
    }
  }

  const logAdminAction = async (action: string, row: QueueRow, details: string) => {
    try {
      await addDoc(collection(db, 'admin_activity_logs'), {
        adminId: currentAdmin(),
        action,
        targetUserId: resolveOwner(row).id,
        targetUserName: resolveOwner(row).name,
        details,
        timestamp: Timestamp.now()
      })
    } catch (e) {
      console.warn('تعذر تسجيل نشاط الأدمن:', e)
    }
  }

  /** إخفاء/تعطيل المحتوى المُبلَّغ عنه حسب نوعه */
  const removeTargetContent = async (row: QueueRow) => {
    if (!row.targetId) return
    const moderation = {
      moderationRemoved: true,
      moderationRemovedAt: Timestamp.now(),
      moderationRemovedBy: currentAdmin(),
      moderationReason: row.reason || '',
      updatedAt: Timestamp.now()
    }

    if (row.targetType === 'request') {
      await updateDoc(doc(db, 'requests', row.targetId), { ...moderation, status: 'cancelled' })
    } else if (row.targetType === 'offer') {
      await updateDoc(doc(db, 'offers', row.targetId), { ...moderation, status: 'rejected' })
    } else if (row.targetType === 'product') {
      await updateDoc(doc(db, 'products', row.targetId), { ...moderation, isActive: false })
    } else if (row.targetType === 'user_profile') {
      await updateDoc(doc(db, 'users', row.targetId), { ...moderation, profileImage: '' })
    } else if (row.targetType === 'store_activation') {
      await updateDoc(doc(db, 'store_activation_requests', row.targetId), moderation)
    }
  }

  /** نفس منطق صفحة المستخدمين لتغيير حالة الحساب */
  const setUserAccountStatus = async (
    userId: string,
    newStatus: 'active' | 'inactive',
    reason: string
  ) => {
    const updateData: Record<string, unknown> = {
      status: newStatus,
      isActive: newStatus === 'active',
      updatedAt: Timestamp.now()
    }
    if (newStatus === 'inactive') {
      updateData.deactivatedBy = 'admin'
      updateData.deactivatedAt = Timestamp.now()
      updateData.deactivatedReason = reason || 'تم التعطيل بواسطة الإدارة'
    } else {
      updateData.deactivatedBy = null
      updateData.deactivatedAt = null
      updateData.deactivatedReason = null
    }
    await updateDoc(doc(db, 'users', userId), updateData)
  }

  const runAction = async () => {
    const row = confirmDialog.row
    const action = confirmDialog.action
    if (!row || !action) return

    setWorking(true)
    try {
      if (action === 'remove') {
        await removeTargetContent(row)
        await markReviewed(row, 'resolved_removed', reviewNote)
        await logAdminAction('حذف محتوى مخالف', row, reviewNote || `تم حذف ${targetTypeLabels[row.targetType] || row.targetType}`)
        setSnackbar({ open: true, message: 'تم حذف المحتوى وإغلاق البلاغ', severity: 'success' })
      } else if (action === 'deactivate' || action === 'activate') {
        const owner = resolveOwner(row)
        if (!owner.id) {
          setSnackbar({ open: true, message: 'لا يوجد مستخدم مرتبط بهذا البلاغ', severity: 'error' })
          setWorking(false)
          return
        }
        if (row.reporterId && owner.id === row.reporterId) {
          setSnackbar({
            open: true,
            message: 'تحذير: معرّف المُبلَّغ عنه يطابق المبلّغ — راجع التفاصيل قبل المتابعة',
            severity: 'error'
          })
          setWorking(false)
          return
        }
        const newStatus = action === 'activate' ? 'active' : 'inactive'
        await setUserAccountStatus(
          owner.id,
          newStatus,
          reviewNote ||
            (newStatus === 'inactive' ? 'تعطيل بسبب نشر محتوى مخالف' : 'تم التفعيل بواسطة الإدارة')
        )
        if (newStatus === 'inactive') {
          await markReviewed(row, 'resolved_removed', reviewNote)
        }
        await logAdminAction(
          newStatus === 'active' ? 'تفعيل مستخدم' : 'تعطيل مستخدم',
          row,
          reviewNote || `تم ${newStatus === 'active' ? 'تفعيل' : 'تعطيل'} المُبلَّغ عنه`
        )
        setSnackbar({
          open: true,
          message: newStatus === 'active' ? 'تم تفعيل المُبلَّغ عنه' : 'تم تعطيل المُبلَّغ عنه',
          severity: 'success'
        })
      } else {
        await markReviewed(row, 'resolved_kept', reviewNote)
        await logAdminAction('رفض بلاغ', row, reviewNote || 'تمت المراجعة ولا يوجد مخالفة')
        setSnackbar({ open: true, message: 'تم رفض البلاغ والإبقاء على المحتوى', severity: 'success' })
      }
      setConfirmDialog({ open: false })
      setReviewNote('')
      setStatusFilter('all')
      setPage(1)
    } catch (error) {
      console.error('خطأ في تنفيذ إجراء البلاغ:', error)
      setSnackbar({ open: true, message: 'حدث خطأ أثناء تنفيذ الإجراء', severity: 'error' })
    } finally {
      setWorking(false)
    }
  }

  const updateReportStatusFromDetails = async (row: QueueRow, newStatus: QueueStatus) => {
    setWorking(true)
    try {
      await markReviewed(row, newStatus, reviewNote || `تعديل الحالة إلى ${newStatus}`)
      await logAdminAction('تعديل حالة بلاغ', row, `الحالة الجديدة: ${newStatus}`)
      setDetailsDialog((prev) =>
        prev.row ? { ...prev, row: { ...prev.row, status: newStatus } } : prev
      )
      setSnackbar({ open: true, message: 'تم تحديث حالة البلاغ', severity: 'success' })
    } catch (e) {
      console.error(e)
      setSnackbar({ open: true, message: 'تعذر تحديث حالة البلاغ', severity: 'error' })
    } finally {
      setWorking(false)
    }
  }

  const deleteRecords = async () => {
    const toDelete = deleteDialog.rows
    if (!toDelete.length) return
    setWorking(true)
    try {
      const batch = writeBatch(db)
      toDelete.forEach((row) => {
        batch.delete(doc(db, row.collectionName, row.id))
      })
      await batch.commit()
      setSelectedKeys((prev) => prev.filter((k) => !toDelete.some((r) => rowKey(r) === k)))
      setDeleteDialog({ open: false, rows: [] })
      setSnackbar({ open: true, message: `تم حذف ${toDelete.length} سجل`, severity: 'success' })
    } catch (e) {
      console.error(e)
      setSnackbar({ open: true, message: 'تعذر حذف السجلات', severity: 'error' })
    } finally {
      setWorking(false)
    }
  }

  const toggleSelect = (row: QueueRow) => {
    const k = rowKey(row)
    setSelectedKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))
  }

  const toggleSelectAllPage = () => {
    const pageKeys = paginatedRows.map(rowKey)
    const allSelected = pageKeys.every((k) => selectedKeys.includes(k))
    if (allSelected) {
      setSelectedKeys((prev) => prev.filter((k) => !pageKeys.includes(k)))
    } else {
      setSelectedKeys((prev) => [...new Set([...prev, ...pageKeys])])
    }
  }

  const confirmTexts: Record<
    'remove' | 'deactivate' | 'activate' | 'keep',
    { title: string; body: string; button: string; color: 'error' | 'warning' | 'success' }
  > = {
    remove: {
      title: 'تأكيد حذف المحتوى',
      body: 'سيتم إخفاء المحتوى المخالف من التطبيق وإغلاق البلاغ. هل تريد المتابعة؟',
      button: 'حذف المحتوى',
      color: 'error'
    },
    deactivate: {
      title: 'تأكيد تعطيل المُبلَّغ عنه',
      body: 'سيتم تعطيل حساب صاحب المحتوى المخالف (المُبلَّغ عنه) وليس من أرسل البلاغ. الحالة تظهر فوراً في صفحة المستخدمين أيضاً.',
      button: 'تعطيل المُبلَّغ عنه',
      color: 'warning'
    },
    activate: {
      title: 'تأكيد تفعيل المُبلَّغ عنه',
      body: 'سيتم إعادة تفعيل حساب المُبلَّغ عنه. الحالة تظهر فوراً في صفحة المستخدمين أيضاً.',
      button: 'تفعيل المُبلَّغ عنه',
      color: 'success'
    },
    keep: {
      title: 'تأكيد رفض البلاغ',
      body: 'سيتم إغلاق البلاغ مع الإبقاء على المحتوى كما هو. هل تريد المتابعة؟',
      button: 'رفض البلاغ',
      color: 'success'
    }
  }

  const renderThumb = (row: QueueRow) => {
    const url = resolveImage(row)
    if (!url) {
      return (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 1,
            bgcolor: 'grey.100',
            border: '1px dashed',
            borderColor: 'grey.400',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 0.5
          }}
        >
          <NoImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.1 }}>
            {row.deletedFromStorage ? 'حُذفت تلقائياً' : 'لا توجد صورة'}
          </Typography>
        </Box>
      )
    }
    return (
      <Box
        component="img"
        src={url}
        alt="المحتوى المُبلَّغ عنه"
        onClick={() => setImageDialog({ open: true, url })}
        sx={{
          width: 64,
          height: 64,
          objectFit: 'cover',
          borderRadius: 1,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider'
        }}
      />
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          البلاغات والمحتوى المخالف
        </Typography>
        <Stack direction="row" spacing={1}>
          {selectedKeys.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteRecordIcon />}
              onClick={() => {
                const rowsToDelete = rows.filter((r) => selectedKeys.includes(rowKey(r)))
                setDeleteDialog({ open: true, rows: rowsToDelete })
              }}
            >
              حذف المحدد ({selectedKeys.length})
            </Button>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        يجب مراجعة كل بلاغ جديد خلال 24 ساعة واتخاذ إجراء: حذف المحتوى، أو تعطيل المستخدم، أو رفض البلاغ.
      </Alert>

      {flagsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {flagsError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="بحث بالاسم أو الهاتف أو السبب..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>حالة البلاغ</InputLabel>
                <Select
                  value={statusFilter}
                  label="حالة البلاغ"
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter)
                    setPage(1)
                  }}
                >
                  <MenuItem value="pending">قيد المراجعة</MenuItem>
                  <MenuItem value="resolved_removed">تم حذف المحتوى</MenuItem>
                  <MenuItem value="resolved_kept">تم رفض البلاغ</MenuItem>
                  <MenuItem value="all">الكل</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>مصدر البلاغ</InputLabel>
                <Select
                  value={sourceFilter}
                  label="مصدر البلاغ"
                  onChange={(e) => {
                    setSourceFilter(e.target.value as SourceFilter)
                    setPage(1)
                  }}
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="user_report">بلاغ من مستخدم</MenuItem>
                  <MenuItem value="auto_vision">فحص تلقائي</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack spacing={1}>
                <Chip icon={<FilterIcon />} label={`إجمالي: ${filteredRows.length}`} color="primary" variant="outlined" />
                <Chip label={`قيد المراجعة: ${pendingCount}`} color={pendingCount > 0 ? 'warning' : 'default'} variant="outlined" />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    paginatedRows.some((r) => selectedKeys.includes(rowKey(r))) &&
                    !paginatedRows.every((r) => selectedKeys.includes(rowKey(r)))
                  }
                  checked={
                    paginatedRows.length > 0 &&
                    paginatedRows.every((r) => selectedKeys.includes(rowKey(r)))
                  }
                  onChange={toggleSelectAllPage}
                />
              </TableCell>
              <TableCell><strong>التاريخ</strong></TableCell>
              <TableCell><strong>المصدر</strong></TableCell>
              <TableCell><strong>الصورة</strong></TableCell>
              <TableCell><strong>السبب</strong></TableCell>
              <TableCell><strong>المحتوى</strong></TableCell>
              <TableCell><strong>المُبلَّغ عنه</strong></TableCell>
              <TableCell><strong>الحالة</strong></TableCell>
              <TableCell><strong>الإجراءات</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">لا توجد بلاغات</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => {
                const owner = resolveOwner(row)
                return (
                  <TableRow key={rowKey(row)} hover selected={selectedKeys.includes(rowKey(row))}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedKeys.includes(rowKey(row))}
                        onChange={() => toggleSelect(row)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.source === 'user_report' ? 'بلاغ من مستخدم' : 'فحص تلقائي'}
                        color={row.source === 'user_report' ? 'info' : 'secondary'}
                        size="small"
                      />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {targetTypeLabels[row.targetType] || row.targetType}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderThumb(row)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatReason(row.reason)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {resolveText(row) || 'بدون نص'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ cursor: owner.id ? 'pointer' : 'default' }}
                        onClick={() => owner.id && openUserAccount(owner.id, owner.phone)}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={owner.id ? 'primary' : 'text.primary'}
                          sx={{ textDecoration: owner.id ? 'underline' : 'none' }}
                        >
                          {owner.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {owner.phone}
                        </Typography>
                        <Chip
                          size="small"
                          sx={{ mt: 0.5 }}
                          label={owner.status === 'active' ? 'نشط' : 'غير نشط'}
                          color={owner.status === 'active' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {statusMap[row.status] ? (
                        <Chip label={statusMap[row.status].label} color={statusMap[row.status].color} size="small" />
                      ) : (
                        <Chip label="غير محدد" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="عرض التفاصيل">
                          <IconButton size="small" color="primary" onClick={() => setDetailsDialog({ open: true, row })}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف المحتوى المخالف">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setReviewNote('')
                              setConfirmDialog({ open: true, row, action: 'remove' })
                            }}
                          >
                            <DeleteContentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            owner.status === 'active'
                              ? 'تعطيل المُبلَّغ عنه'
                              : 'تفعيل المُبلَّغ عنه'
                          }
                        >
                          <IconButton
                            size="small"
                            color={owner.status === 'active' ? 'warning' : 'success'}
                            onClick={() => {
                              setReviewNote('')
                              setConfirmDialog({
                                open: true,
                                row,
                                action: owner.status === 'active' ? 'deactivate' : 'activate'
                              })
                            }}
                          >
                            {owner.status === 'active' ? <BlockUserIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="رفض البلاغ">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setReviewNote('')
                              setConfirmDialog({ open: true, row, action: 'keep' })
                            }}
                          >
                            <KeepIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف سجل البلاغ">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, rows: [row] })}
                          >
                            <DeleteRecordIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size="large" />
        </Box>
      )}

      {/* تفاصيل البلاغ */}
      <Dialog open={detailsDialog.open} onClose={() => setDetailsDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">تفاصيل البلاغ</Typography>
            <IconButton onClick={() => setDetailsDialog({ open: false })}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailsDialog.row && (() => {
            const row = detailsDialog.row!
            const owner = resolveOwner(row)
            const reporter = resolveReporter(row)
            const content = resolveContent(row)
            const images = resolveImages(row)
            const requestId = content.requestId || row.requestId || (row.targetType === 'request' ? row.targetId : '')

            const PersonCard = ({
              title,
              person
            }: {
              title: string
              person: PersonInfo
            }) => (
              <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
                {person.id ? (
                  <Button
                    variant="text"
                    color="primary"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => openUserAccount(person.id, person.phone)}
                    sx={{ px: 0, justifyContent: 'flex-start', textAlign: 'right', display: 'block' }}
                  >
                    <Typography variant="body1" fontWeight="bold">{person.name}</Typography>
                  </Button>
                ) : (
                  <Typography variant="body1" fontWeight="medium">{person.name}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{person.phone}</Typography>
                {person.id && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    المعرّف: {person.id}
                  </Typography>
                )}
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  label={person.status === 'active' ? 'الحساب نشط' : 'الحساب غير نشط'}
                  color={person.status === 'active' ? 'success' : 'error'}
                />
              </Paper>
            )

            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">المصدر</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {row.source === 'user_report' ? 'بلاغ من مستخدم' : 'فحص تلقائي'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">التاريخ</Typography>
                    <Typography variant="body1">{formatDate(row.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">نوع المحتوى</Typography>
                    <Typography variant="body1">
                      {targetTypeLabels[row.targetType] || row.targetType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">السبب</Typography>
                    <Typography variant="body1">{formatReason(row.reason)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">رقم / معرّف الطلب</Typography>
                    {requestId ? (
                      <Button
                        variant="text"
                        color="primary"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => openRequestOrders(requestId)}
                        sx={{ px: 0, justifyContent: 'flex-start', textAlign: 'right', display: 'block' }}
                      >
                        <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {requestId}
                        </Typography>
                      </Button>
                    ) : (
                      <Typography variant="body1">غير متوفر</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">معرّف المحتوى المُبلَّغ عنه</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {row.targetId || 'غير متوفر'}
                      {row.offerId ? ` (عرض)` : ''}
                    </Typography>
                  </Grid>
                  {content.productName && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">اسم المنتج / الطلب</Typography>
                      <Typography variant="body1" fontWeight="medium">{content.productName}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      حالة البلاغ (عدّلها مباشرة من هنا)
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <Select
                        value={
                          ['pending', 'resolved_removed', 'resolved_kept'].includes(row.status)
                            ? row.status
                            : 'pending'
                        }
                        disabled={working}
                        onChange={(e) =>
                          updateReportStatusFromDetails(row, e.target.value as QueueStatus)
                        }
                      >
                        <MenuItem value="pending">قيد المراجعة</MenuItem>
                        <MenuItem value="resolved_removed">تم حذف المحتوى</MenuItem>
                        <MenuItem value="resolved_kept">تم رفض البلاغ</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PersonCard
                      title={`المُبلِّغ — من أرسل البلاغ${roleLabel(row.reporterRole, row.targetType) ? ` (${roleLabel(row.reporterRole, row.targetType)})` : ''}`}
                      person={reporter}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PersonCard
                      title={`المُبلَّغ عنه — صاحب المحتوى المخالف${
                        row.targetType === 'offer' ? ' (تاجر)' : row.targetType === 'request' ? ' (مشتري)' : ''
                      }`}
                      person={owner}
                    />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    المحتوى المُبلَّغ عنه (للتحقق)
                  </Typography>
                  {(content.offerNotes || content.offerPrice != null) && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'warning.50' }}>
                      <Typography variant="caption" color="text.secondary">تفاصيل العرض</Typography>
                      {content.offerPrice != null && (
                        <Typography variant="body1" fontWeight="bold">
                          السعر: {content.offerPrice}
                        </Typography>
                      )}
                      {content.offerNotes && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          {content.offerNotes}
                        </Typography>
                      )}
                    </Paper>
                  )}
                  {(content.requestNotes || content.additionalDetails) && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary">نصوص مرتبطة بالطلب</Typography>
                      {content.requestNotes && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          ملاحظات: {content.requestNotes}
                        </Typography>
                      )}
                      {content.additionalDetails && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          تفاصيل إضافية: {content.additionalDetails}
                        </Typography>
                      )}
                    </Paper>
                  )}
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 220, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">النص الكامل</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {content.text || resolveText(row) || 'لا يوجد نص'}
                    </Typography>
                  </Paper>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    الصور المُبلَّغ عنها / المرتبطة بالمحتوى
                  </Typography>
                  {images.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {images.map((url) => (
                        <Box
                          key={url}
                          component="img"
                          src={url}
                          alt="المحتوى المُبلَّغ عنه"
                          onClick={() => setImageDialog({ open: true, url })}
                          sx={{
                            width: 140,
                            height: 140,
                            objectFit: 'cover',
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        {row.deletedFromStorage
                          ? 'تم حذف الصورة تلقائياً من التخزين بعد اكتشاف المخالفة.'
                          : row.targetType === 'offer'
                            ? 'هذا العرض نصّي (سعر/ملاحظات) ولا يحتوي على صورة مرفقة. راجع النص أعلاه للتحقق.'
                            : 'لا توجد صورة مرفقة بهذا البلاغ.'}
                      </Typography>
                    </Paper>
                  )}
                </Box>

                {row.visionScores && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">نتائج الفحص التلقائي</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {Object.entries(row.visionScores).map(([k, v]) => (
                        <Chip key={k} size="small" label={`${k}: ${v}`} variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}

                {row.reviewedBy && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">تمت المراجعة بواسطة</Typography>
                    <Typography variant="body2">
                      {row.reviewedBy} — {formatDate(row.reviewedAt)}
                    </Typography>
                    {row.reviewNote && (
                      <Typography variant="body2" color="text.secondary">{row.reviewNote}</Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )
          })()}
        </DialogContent>
        <DialogActions>
          {detailsDialog.row && (
            <>
              <Button
                startIcon={<DeleteContentIcon />}
                variant="contained"
                color="error"
                onClick={() => {
                  setReviewNote('')
                  setConfirmDialog({ open: true, row: detailsDialog.row, action: 'remove' })
                }}
              >
                حذف المحتوى
              </Button>
              <Button
                startIcon={
                  resolveOwner(detailsDialog.row).status === 'active' ? (
                    <BlockUserIcon />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                variant="contained"
                color={resolveOwner(detailsDialog.row).status === 'active' ? 'warning' : 'success'}
                onClick={() => {
                  setReviewNote('')
                  setConfirmDialog({
                    open: true,
                    row: detailsDialog.row,
                    action:
                      resolveOwner(detailsDialog.row!).status === 'active'
                        ? 'deactivate'
                        : 'activate'
                  })
                }}
              >
                {resolveOwner(detailsDialog.row).status === 'active'
                  ? 'تعطيل المُبلَّغ عنه'
                  : 'تفعيل المُبلَّغ عنه'}
              </Button>
              <Button
                startIcon={<KeepIcon />}
                variant="outlined"
                color="success"
                onClick={() => {
                  setReviewNote('')
                  setConfirmDialog({ open: true, row: detailsDialog.row, action: 'keep' })
                }}
              >
                رفض البلاغ
              </Button>
              <Button
                startIcon={<DeleteRecordIcon />}
                color="inherit"
                onClick={() =>
                  detailsDialog.row && setDeleteDialog({ open: true, rows: [detailsDialog.row] })
                }
              >
                حذف السجل
              </Button>
            </>
          )}
          <Button onClick={() => setDetailsDialog({ open: false })}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* تكبير الصورة */}
      <Dialog open={imageDialog.open} onClose={() => setImageDialog({ open: false })} maxWidth="md">
        <DialogContent sx={{ p: 1 }}>
          {imageDialog.url && (
            <Box component="img" src={imageDialog.url} alt="المحتوى المُبلَّغ عنه" sx={{ maxWidth: '100%', display: 'block' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog({ open: false })}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* تأكيد الإجراء */}
      <Dialog open={confirmDialog.open} onClose={() => !working && setConfirmDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmDialog.action ? confirmTexts[confirmDialog.action].title : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.action ? confirmTexts[confirmDialog.action].body : ''}</DialogContentText>
          {confirmDialog.action === 'deactivate' && confirmDialog.row && (
            <Alert severity="info" sx={{ mt: 2 }}>
              سيتم تعطيل المُبلَّغ عنه فقط: {resolveOwner(confirmDialog.row).name} — وليس المبلّغ.
            </Alert>
          )}
          {confirmDialog.action === 'activate' && confirmDialog.row && (
            <Alert severity="info" sx={{ mt: 2 }}>
              سيتم تفعيل المُبلَّغ عنه: {resolveOwner(confirmDialog.row).name}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            label="ملاحظة المراجعة (اختياري)"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={working} onClick={() => setConfirmDialog({ open: false })}>إلغاء</Button>
          <Button
            variant="contained"
            disabled={working}
            color={confirmDialog.action ? confirmTexts[confirmDialog.action].color : 'primary'}
            onClick={runAction}
          >
            {confirmDialog.action ? confirmTexts[confirmDialog.action].button : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حذف سجل/سجلات البلاغ */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !working && setDeleteDialog({ open: false, rows: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تأكيد حذف السجل</DialogTitle>
        <DialogContent>
          <DialogContentText>
            سيتم حذف {deleteDialog.rows.length} سجل من قائمة البلاغات نهائياً. هذا لا يحذف محتوى
            التطبيق تلقائياً — فقط سجل البلاغ من لوحة التحكم.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={working} onClick={() => setDeleteDialog({ open: false, rows: [] })}>
            إلغاء
          </Button>
          <Button variant="contained" color="error" disabled={working} onClick={deleteRecords}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
